import React from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

interface RevealOnScrollProps {
  children: React.ReactNode;
  className?: string;
  direction?: 'up' | 'down' | 'left' | 'right' | 'fade';
  delay?: number;
  duration?: number;
  threshold?: number;
  triggerOnce?: boolean;
  as?: keyof JSX.IntrinsicElements;
}

const directionClasses = {
  up: {
    hidden: 'opacity-0 translate-y-8',
    visible: 'opacity-100 translate-y-0',
  },
  down: {
    hidden: 'opacity-0 -translate-y-8',
    visible: 'opacity-100 translate-y-0',
  },
  left: {
    hidden: 'opacity-0 translate-x-8',
    visible: 'opacity-100 translate-x-0',
  },
  right: {
    hidden: 'opacity-0 -translate-x-8',
    visible: 'opacity-100 translate-x-0',
  },
  fade: {
    hidden: 'opacity-0',
    visible: 'opacity-100',
  },
};

export const RevealOnScroll: React.FC<RevealOnScrollProps> = ({
  children,
  className = '',
  direction = 'up',
  delay = 0,
  duration = 500,
  threshold = 0.1,
  triggerOnce = true,
  as: Component = 'div',
}) => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>({
    threshold,
    triggerOnce,
  });

  const directionStyle = directionClasses[direction];
  const animationClass = isVisible ? directionStyle.visible : directionStyle.hidden;

  return (
    <Component
      ref={ref as any}
      className={`transition-all ease-out ${animationClass} ${className}`}
      style={{
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </Component>
  );
};

// Staggered children reveal
interface StaggeredRevealProps {
  children: React.ReactNode[];
  className?: string;
  childClassName?: string;
  staggerDelay?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'fade';
  duration?: number;
  threshold?: number;
}

export const StaggeredReveal: React.FC<StaggeredRevealProps> = ({
  children,
  className = '',
  childClassName = '',
  staggerDelay = 100,
  direction = 'up',
  duration = 500,
  threshold = 0.1,
}) => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>({
    threshold,
    triggerOnce: true,
  });

  const directionStyle = directionClasses[direction];

  return (
    <div ref={ref} className={className}>
      {React.Children.map(children, (child, index) => {
        const animationClass = isVisible ? directionStyle.visible : directionStyle.hidden;

        return (
          <div
            className={`transition-all ease-out ${animationClass} ${childClassName}`}
            style={{
              transitionDuration: `${duration}ms`,
              transitionDelay: `${index * staggerDelay}ms`,
            }}
          >
            {child}
          </div>
        );
      })}
    </div>
  );
};

// Section reveal with title
interface SectionRevealProps {
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  titleClassName?: string;
}

export const SectionReveal: React.FC<SectionRevealProps> = ({
  title,
  subtitle,
  children,
  className = '',
  titleClassName = '',
}) => {
  return (
    <section className={className}>
      {(title || subtitle) && (
        <RevealOnScroll className="text-center mb-12" direction="up">
          {title && (
            <h2 className={`text-3xl md:text-4xl font-bold text-text-primary-light dark:text-text-primary-dark mb-4 ${titleClassName}`}>
              {title}
            </h2>
          )}
          {subtitle && (
            <p className="text-lg text-text-secondary-light dark:text-text-secondary-dark max-w-2xl mx-auto">
              {subtitle}
            </p>
          )}
        </RevealOnScroll>
      )}
      {children}
    </section>
  );
};

export default RevealOnScroll;
